/**
 * ダイヤ・コースマスタのコース（log_web_issues#347）。API の形は docs/api_v5_master_courses.md。
 * 便ごとに 1 行（code × tripNo）。2 便以降は firstTripCourse で 1 便に紐付く（設計書 C2・C3）
 */
export type TruckRef = { id: number; truckNumber: string | null; licensePlateValue: string };
export type DriverRef = { id: number; name: string };
export type PartnerCompanyRef = { id: number; name: string };
export type TimetableRef = { id: number; name: string };
export type PlaceRef = { id: number; internalLocationCode: string | null; name: string; address: string };
/** 地点の参照の API の形（地点の検索・ルートの地点）。地点コードは internal_location_code（#376） */
export type PlaceRefResponse = { id: number; internal_location_code: string | null; name: string; address: string };
export type CourseStatus = 'active' | 'inactive';
export type CourseRef = { id: number; code: string; name: string | null };
export type LinkedTripEntity = { id: number; code: string; tripNo: number; name: string | null; status: CourseStatus };

export type CourseEntity = {
  id: number;
  code: string;
  name: string | null;
  tripNo: number;
  status: CourseStatus;
  sortOrder: number;
  firstTripCourse: CourseRef | null;
  defaultTruck: TruckRef | null;
  defaultDriver: DriverRef | null;
  partnerCompany: PartnerCompanyRef | null;
  /** 所属ダイヤ（id 昇順。未所属だけなら []） */
  timetables: TimetableRef[];
  createdAt: string;
  updatedAt: string;
};

export type CourseStopEntity = {
  id: number;
  sequenceNo: number;
  place: PlaceRef;
  arrivalTime: string | null;
  departureTime: string | null;
  drivingDurationSeconds: number | null;
  drivingDistanceMm: number | null;
  /** 対象外。そのダイヤの並びに行を残したまま行かない */
  excluded: boolean;
  /** 適用終了日（YYYY-MM-DD）。履歴として残す */
  endedOn: string | null;
};

/** ルート ＝ コース × ダイヤの地点の並び。timetable が null は「未所属の並び」（コースに 1 つ） */
export type CourseRouteEntity = {
  id: number;
  timetable: TimetableRef | null;
  sortOrder: number;
  loadingPlace: PlaceRef | null;
  loadingArrivalTime: string | null;
  loadingDepartureTime: string | null;
  loadingDrivingDurationSeconds: number | null;
  loadingDrivingDistanceMm: number | null;
  stops: CourseStopEntity[];
  updatedAt: string;
};

export type CourseDetailEntity = CourseEntity & {
  depotDepartureStartTime: string | null;
  depotDepartureEndTime: string | null;
  depotArrivalStartTime: string | null;
  depotArrivalEndTime: string | null;
  /** 紐付く 2 便以降（trip_no 昇順）。空なら削除できる */
  linkedTrips: LinkedTripEntity[];
  routes: CourseRouteEntity[];
};

// ---- API の形（snake_case）----
export type TruckRefResponse = { id: number; truck_number: string | null; license_plate_value: string };
export type SharedColumnsResponse = {
  default_truck: TruckRefResponse | null;
  default_driver: DriverRef | null;
  partner_company: PartnerCompanyRef | null;
};
export type CourseResponse = SharedColumnsResponse & {
  id: number; code: string; name: string | null; trip_no: number; status: CourseStatus; sort_order: number;
  first_trip_course: CourseRef | null; timetables: TimetableRef[]; created_at: string; updated_at: string;
};
export type CourseStopResponse = {
  id: number; sequence_no: number; place: PlaceRefResponse; arrival_time: string | null; departure_time: string | null;
  driving_duration_seconds: number | null; driving_distance_mm: number | null; excluded: boolean; ended_on: string | null;
};
export type CourseRouteResponse = {
  id: number; timetable: TimetableRef | null; sort_order: number; loading_place: PlaceRefResponse | null;
  loading_arrival_time: string | null; loading_departure_time: string | null;
  loading_driving_duration_seconds: number | null; loading_driving_distance_mm: number | null;
  stops: CourseStopResponse[]; updated_at: string;
};
export type CourseDetailResponse = CourseResponse & {
  depot_departure_start_time: string | null; depot_departure_end_time: string | null;
  depot_arrival_start_time: string | null; depot_arrival_end_time: string | null;
  linked_trips: { id: number; code: string; trip_no: number; name: string | null; status: CourseStatus }[];
  routes: CourseRouteResponse[];
};

export const deserializeShared = (data: SharedColumnsResponse) => ({
  defaultTruck: data.default_truck
    ? { id: data.default_truck.id, truckNumber: data.default_truck.truck_number, licensePlateValue: data.default_truck.license_plate_value }
    : null,
  defaultDriver: data.default_driver,
  partnerCompany: data.partner_company,
});

export const deserializeCourse = (data: CourseResponse): CourseEntity => ({
  id: data.id, code: data.code, name: data.name, tripNo: data.trip_no, status: data.status, sortOrder: data.sort_order,
  firstTripCourse: data.first_trip_course, ...deserializeShared(data), timetables: data.timetables,
  createdAt: data.created_at, updatedAt: data.updated_at,
});

export const deserializePlaceRef = (p: PlaceRefResponse): PlaceRef => ({
  id: p.id, internalLocationCode: p.internal_location_code, name: p.name, address: p.address,
});

export const deserializeStop = (s: CourseStopResponse): CourseStopEntity => ({
  id: s.id, sequenceNo: s.sequence_no, place: deserializePlaceRef(s.place), arrivalTime: s.arrival_time, departureTime: s.departure_time,
  drivingDurationSeconds: s.driving_duration_seconds, drivingDistanceMm: s.driving_distance_mm, excluded: s.excluded, endedOn: s.ended_on,
});

export const deserializeRoute = (r: CourseRouteResponse): CourseRouteEntity => ({
  id: r.id, timetable: r.timetable, sortOrder: r.sort_order, loadingPlace: r.loading_place && deserializePlaceRef(r.loading_place),
  loadingArrivalTime: r.loading_arrival_time, loadingDepartureTime: r.loading_departure_time,
  loadingDrivingDurationSeconds: r.loading_driving_duration_seconds, loadingDrivingDistanceMm: r.loading_driving_distance_mm,
  stops: r.stops.map(deserializeStop), updatedAt: r.updated_at,
});

export const deserializeCourseDetail = (d: CourseDetailResponse): CourseDetailEntity => ({
  ...deserializeCourse(d),
  depotDepartureStartTime: d.depot_departure_start_time, depotDepartureEndTime: d.depot_departure_end_time,
  depotArrivalStartTime: d.depot_arrival_start_time, depotArrivalEndTime: d.depot_arrival_end_time,
  linkedTrips: d.linked_trips.map((t) => ({ id: t.id, code: t.code, tripNo: t.trip_no, name: t.name, status: t.status })),
  routes: d.routes.map(deserializeRoute),
});

// ---- リクエスト（API に snake_case のまま送る）----
export type CoursePayload = Partial<{
  code: string; name: string | null; trip_no: number; first_trip_course_id: number | null;
  /** 紐付け先と 3 列がずれているときだけ意味を持つ（422 trip_group_mismatch の後に付けて送り直す） */
  align: 'self' | 'target';
  default_truck_id: number | null; default_driver_id: number | null; partner_company_id: number | null;
  depot_departure_start_time: string | null; depot_departure_end_time: string | null;
  depot_arrival_start_time: string | null; depot_arrival_end_time: string | null;
}>;

/** 各要素は全状態で送る（送らなかった時刻は API が NULL にする）。id 無しは追加 */
export type StopPayload = {
  id?: number; place_id: number; arrival_time: string | null; departure_time: string | null;
  driving_duration_seconds: number | null; driving_distance_mm: number | null; excluded: boolean;
};
export type RoutePayload = Partial<{
  loading_place_id: number | null; loading_arrival_time: string | null; loading_departure_time: string | null;
  loading_driving_duration_seconds: number | null; loading_driving_distance_mm: number | null;
  stops: StopPayload[];
}>;
